import { VtsInterface } from '../../Interface/Vts';

export type VtsPhase =
  | 'before'
  | 'order'
  | 'ordered'
  | 'confirmation'
  | 'confirmed';

type VtsDates = {
  orderStart: Date;
  orderEnd: Date;
  confirmationStart: Date;
  confirmationEnd: Date;
};

export const getVtsPhase = (
  vts: VtsInterface,
  now: Date = new Date()
): VtsPhase => {
  const { orderStart, orderEnd, confirmationStart, confirmationEnd } =
    getVtsDates(vts);
  if (now < orderStart) return 'before';
  if (now < orderEnd) return 'order';
  if (now < confirmationStart) return 'ordered';
  if (now < confirmationEnd) return 'confirmation';
  return 'confirmed';
};

export const getPhaseDeadline = (
  vts: VtsInterface,
  phase: VtsPhase
): Date | null => {
  const { orderStart, orderEnd, confirmationStart, confirmationEnd } =
    getVtsDates(vts);
  switch (phase) {
    case 'before':
      return orderStart;
    case 'order':
      return orderEnd;
    case 'ordered':
      return confirmationStart;
    case 'confirmation':
      return confirmationEnd;
    case 'confirmed':
    default:
      return null;
  }
};

export const formatTimeUntil = (target: Date, now: Date = new Date()): string => {
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return 'now';
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 2) return `in ${days} days`;
  if (days === 1) return 'in 1 day';
  if (hours >= 2) return `in ${hours} hours`;
  if (hours === 1) return 'in 1 hour';
  if (minutes >= 2) return `in ${minutes} minutes`;
  return 'in less than a minute';
};

const padTwoDigits = (value: number): string =>
  value.toString().padStart(2, '0');

export const getVtsDates = (vts: VtsInterface): VtsDates => ({
  orderStart: new Date(vts.order_start),
  orderEnd: new Date(vts.order_end),
  confirmationStart: new Date(vts.confirmation_start),
  confirmationEnd: new Date(vts.confirmation_end),
});

export const formatShortRange = (orderStart: Date, orderEnd: Date): string => {
  const startMonth = orderStart
    .toLocaleString('en-US', { month: 'short' })
    .toUpperCase();
  const endMonth = orderEnd
    .toLocaleString('en-US', { month: 'short' })
    .toUpperCase();
  const startDay = padTwoDigits(orderStart.getDate());
  const endDay = padTwoDigits(orderEnd.getDate());
  const year = orderStart.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`;
  }

  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
};

export const formatLongRange = (orderStart: Date, orderEnd: Date): string => {
  const startMonth = orderStart.toLocaleString('en-US', { month: 'long' });
  const endMonth = orderEnd.toLocaleString('en-US', { month: 'long' });
  const startDay = padTwoDigits(orderStart.getDate());
  const endDay = padTwoDigits(orderEnd.getDate());
  const year = orderStart.getFullYear();

  if (startMonth === endMonth) {
    return `${startDay}-${endDay} ${startMonth} ${year}`;
  }

  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
};

export const formatDateWithTime = (date: Date): string => {
  const datePart = date.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase();

  return `${datePart} at ${timePart}`;
};
